import React, { useCallback, useEffect, useState } from "react";
import { BsBank } from "react-icons/bs";
import { AiOutlineUserSwitch, AiOutlineMinusCircle, AiOutlineUsergroupAdd } from "react-icons/ai";
import "../Styles/Staff.css";
import Swal from "sweetalert2";
import AddBranch from "../components/Modals/AddBranch";
import AddStaff from "../components/Modals/AddStaff";
import { toast } from "react-toastify";
import ReLoginModal from "../components/ReLoginModal";
import { CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import api from "../util/axios";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import SolarMenu from "../asset/svg/solarMenu";
import { SidebarModal } from "../components/Modals/sidebarModal";

type Branch = {
  name: string;
  state: string;
  city: string;
  address: string;
  phone: string;
  createdAt: string;
  updatedAt: string;
  _id: string;
}

type ApiError = {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
};

const Staff = () => {
  const navigate = useNavigate()
  const [addBranch, setAddBranch] = useState(false);
  const [addStaffs, setAddStaffs] = useState(false);
  const [reLogin, setReLogin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [getBranches, setGetBranches] = useState<Branch[]>([]);
  const [modalDisplay, setModalDisplay] = useState(false);
  const [selectedBranchId, setSelectedBranchId] = useState<string | null>(null);
  const [sign, setSign] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    address: "",
    city: "",
    state: "",
    country: "",
    role: "",
  });
  const [formData, setFormData] = useState<{
    branchId: string,
    name: string,
    state: string,
    city: string,
    address: string,
    phone: string
    createdAt: string,
    updatedAt: string
  }>({
    branchId: "",
    name: "",
    state: "",
    city: "",
    address: "",
    phone: "",
    createdAt: "",
    updatedAt: ""
  });

  const userRole = window.localStorage.getItem("userRole");
  const isAdmin = userRole && userRole.toLowerCase() === "admin";
  const isDisabled = !isAdmin || loading;

  const handleAddBranch = () => {
    setAddBranch(!addBranch);
  };

  const handleAddStaff = () => {
    setAddStaffs(!addStaffs);
  };

  const token = window.localStorage.getItem("token");
  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };


  const handleChange = ({ target: { name, value } }: any) => {
    setSign((prevSign) => ({
      ...prevSign,
      [name]: value,
    }));
  };

  const handleSubmit = (e: any) => {
    setLoading(true);
    e.preventDefault();

    api
      .post(
        "/auth/create/staff",
        {
          firstName: sign.firstName,
          lastName: sign.lastName,
          email: sign.email,
          phone: sign.phone,
          password: sign.password,
          address: sign.address,
          city: sign.city,
          state: sign.state,
          country: sign.country,
          account: {
            role: sign.role,
          },
        },
        { headers: headers }
      )
      .then((res) => {
        setLoading(false);
        setAddStaffs(false);
        Swal.fire({
          title: `Success!`,
          text: `Staff created successfully! Visit the staff page to view.`,
          icon: "success",
          showCancelButton: false,
          confirmButtonColor: "green",
          confirmButtonText: "Continue",
          // cancelButtonText: "Leave Page",
        });
      })
      .catch((err) => {
        Swal.fire({
          title: `Error!`,
          text: `${err.response.data.message}`,
          icon: "error",
          showCancelButton: false,
          confirmButtonColor: "red",
          // cancelButtonColor: "red",
          confirmButtonText: "Try Again",
          // cancelButtonText: "Leave Page",
        });
        if (
          err.response.data.message === "jwt expired" ||
          err.response.data.message === "jwt malformed"
        ) {
          toast.error('Session expired, please log in again.');
          setReLogin(true);
        } else {
          setReLogin(false);
        }
        setLoading(false);
      });
  };

  const fetchBranches = async () => {
    setLoading(true);
    const token = window.localStorage.getItem("token");
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    if (!token) {
      setReLogin(true);
      setLoading(false);
    }
    try {
      const response = await api.get(
        "/branch",
        { headers: headers }
      );
      setGetBranches(response.data.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching branches:", error);
      setLoading(false);
      const err = error as ApiError;
      const errorMessage =
        err.response?.data?.message || err.message || "Failed to fetch branches";
      if (
        errorMessage.includes("jwt expired") ||
        errorMessage.includes("jwt malformed")
      ) {
        setReLogin(true);
      } else {
        setReLogin(false);
      }
    }
  }

  const handleUpdateBranch = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.branchId) {
      toast.error("No branch selected for update!");
      return;
    }
    const token = window.localStorage.getItem("token");
    if (!token) {
      setReLogin(true);
      return;
    }
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    setLoading(true);
    try {
      const response = await api.patch(`/branch/${formData.branchId}`,
        {
          address: formData.address,
          city: formData.city,
          state: formData.state,
          phone: formData.phone,
          name: formData.name
        },
        { headers }
      );
      toast.success("Branch Updated Successfully!");
      setModalDisplay(false);
      fetchBranches();
    } catch (error) {
      const err = error as ApiError;
      const errorMessage =
        err.response?.data?.message || err.message || "Failed to update loading slip";
      console.error("Update error:", errorMessage);
      if (
        errorMessage.includes("jwt expired") ||
        errorMessage.includes("jwt malformed")
      ) {

        setReLogin(true);
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [formData])

  useEffect(() => {
    fetchBranches()
  }, [])

  useEffect(() => {
    if (selectedBranchId && modalDisplay) {
      const branch = getBranches.find((branch) => branch._id === selectedBranchId);
      if (branch) {
        const formattedCreatedAtDate = new Date(branch.createdAt).toLocaleDateString('en-us', {
          year: 'numeric',
          month: 'short',
          day: '2-digit'
        });
        const formattedUpdatedAtDate = new Date(branch.updatedAt).toLocaleDateString('en-us', {
          year: 'numeric',
          month: 'short',
          day: '2-digit'
        });
        setFormData({
          branchId: branch._id || "",
          name: branch.name || "N/A",
          state: branch.state || "N/A",
          city: branch.city || "N/A",
          address: branch.address || "N/A",
          phone: branch.phone || "N/A",
          createdAt: formattedCreatedAtDate || "N/A",
          updatedAt: formattedUpdatedAtDate || "N/A",
        });
      } else {
        toast.error("Selected branch not found. Please try again.");
        setModalDisplay(false);
      }
    }
  }, [selectedBranchId, modalDisplay, getBranches]);

  return (
    <div className="branch">
      {reLogin ? <ReLoginModal /> : null}
      <h2>Branch and Staff Settings</h2>

      <div className="addBranch">
        <button onClick={handleAddBranch} className="addBranchBtn1">
          <BsBank /> Add New Branch
        </button>
        <button onClick={handleAddStaff} className="addBranchBtn3">
          <AiOutlineUserSwitch /> Add New Staff
        </button>
        <button onClick={() => navigate('/dashboard/adminstaff')} className="addBranchBtn1">
          <AiOutlineUsergroupAdd /> View Staff members
        </button>
      </div>

      <div className="branchList">
        {loading ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <CircularProgress />
            <h2 className="!text-black !text-sm">Please wait while we process the request...</h2>
          </div>
        ) : (
          <table>
            <tr>
              <th>
                <AiOutlineMinusCircle />
              </th>
              <th className="!text-blue-500">Branch</th>
              <th className="!text-blue-500">Address</th>
              <th className="!text-blue-500">Actions</th>
            </tr>
            {getBranches.length > 0 ? (
              getBranches.map((branch, index) => (
                <tr key={index} className="taData">
                  <td className="max-w-[80px]">
                    <AiOutlineMinusCircle />
                  </td>
                  <td>{branch.name}</td>
                  <td>{branch.address}</td>
                  <td>
                    <DropdownMenu.Root>
                      <DropdownMenu.Trigger asChild>
                        <button className="">
                          <SolarMenu />
                        </button>
                      </DropdownMenu.Trigger>

                      <DropdownMenu.Portal>
                        <DropdownMenu.Content
                          className="bg-white rounded shadow p-2"
                          sideOffset={5}
                        >
                          <DropdownMenu.Item
                            className="px-2 text-blue-500 py-2 rounded hover:bg-gray-100 focus:outline-none cursor-pointer"
                            onSelect={() => {
                              setSelectedBranchId(branch._id);
                              setModalDisplay(true);
                            }}
                          >
                            View / Edit
                          </DropdownMenu.Item>
                        </DropdownMenu.Content>
                      </DropdownMenu.Portal>
                    </DropdownMenu.Root>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={3} style={{ textAlign: "center" }}>
                  No branches found.
                </td>
              </tr>

            )}
          </table>
        )}
      </div>
      <SidebarModal
        title="View / Edit Branch"
        display={modalDisplay}
        close={() => {
          setModalDisplay(false);
          setFormData({
            branchId: "",
            name: "",
            state: "",
            city: "",
            address: "",
            phone: "",
            createdAt: "",
            updatedAt: ""
          })
        }}
      >
        <div className="invoice-modal-content" style={{ padding: "20px" }}>
          <form onSubmit={handleUpdateBranch}>
            <div style={{ marginBottom: "15px" }}>
              <label style={{ display: "block", marginBottom: "5px" }}>
                Branch Name
              </label>
              <input
                type="text"
                value={formData.name}
                readOnly
                style={{
                  width: "100%",
                  padding: "8px",
                  backgroundColor: "#f0f0f0",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  cursor: "not-allowed",
                  outline: "none",
                }}
              />
            </div>
            <div style={{ marginBottom: "15px" }}>
              <label style={{ display: "block", marginBottom: "5px" }}>
                Address
              </label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    address: e.target.value,
                  }))
                }
                style={{
                  width: "100%",
                  padding: "8px",
                  backgroundColor: "#fff",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  outline: "none",
                }}
              />
            </div>
            <div style={{ marginBottom: "15px" }}>
              <label style={{ display: "block", marginBottom: "5px" }}>
                City
              </label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    city: e.target.value,
                  }))
                }
                style={{
                  width: "100%",
                  padding: "8px",
                  backgroundColor: "#fff",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  outline: "none",
                }}
              />
            </div>
            <div style={{ marginBottom: "15px" }}>
              <label style={{ display: "block", marginBottom: "5px" }}>
                State
              </label>
              <input
                type="text"
                value={formData.state}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    state: e.target.value,
                  }))
                }
                style={{
                  width: "100%",
                  padding: "8px",
                  backgroundColor: "#fff",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  outline: "none",
                }}
              />
            </div>
            <div style={{ marginBottom: "15px" }}>
              <label style={{ display: "block", marginBottom: "5px" }}>
                Phone
              </label>
              <input
                type="text"
                value={formData.phone}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    phone: e.target.value,
                  }))
                }
                style={{
                  width: "100%",
                  padding: "8px",
                  backgroundColor: "#fff",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  outline: "none",
                }}
              />
            </div>
            <div style={{ marginBottom: "15px" }}>
              <label style={{ display: "block", marginBottom: "5px" }}>
                Date Created
              </label>
              <input
                type="text"
                value={formData.createdAt}
                readOnly
                style={{
                  width: "100%",
                  padding: "8px",
                  backgroundColor: "#f0f0f0",
                  border: "1px solid #ccc",
                  cursor: "not-allowed",
                  borderRadius: "4px",
                  outline: "none",
                }}
              />
            </div>
            <div style={{ marginBottom: "15px" }}>
              <label style={{ display: "block", marginBottom: "5px" }}>
                Date Last Updated
              </label>
              <input
                type="text"
                value={formData.updatedAt}
                readOnly
                style={{
                  width: "100%",
                  padding: "8px",
                  backgroundColor: "#f0f0f0",
                  border: "1px solid #ccc",
                  cursor: "not-allowed",
                  borderRadius: "4px",
                  outline: "none",
                }}
              />
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                type="submit"
                disabled={isDisabled}
                style={{
                  padding: "10px 20px",
                  backgroundColor: loading ? "#0B77C5" : "#0B77C5",
                  color: "#fff",
                  border: "none",
                  width: "50%",
                  borderRadius: "4px",
                  cursor: isDisabled ? "not-allowed" : "pointer",
                }}
              >
                {loading ? <CircularProgress size={20} style={{ color: "#fff" }} /> : "Edit"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setModalDisplay(false);
                  setFormData({
                    name: "",
                    state: "",
                    city: "",
                    address: "",
                    phone: "",
                    createdAt: "",
                    updatedAt: "",
                    branchId: ""
                  });
                }}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#ccc",
                  color: "#000",
                  width: "50%",
                  border: "none",
                  borderRadius: "4px",
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </SidebarModal>

      {
        addBranch ?
          <AddBranch
            handleAddBranch={handleAddBranch}
          />
          :
          null
      }

      {
        addStaffs ? (
          <AddStaff
            handleAddStaff={handleAddStaff}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            loading={loading}
            sign={sign}
          />
        ) : null
      }
    </div >
  );
};

export default Staff;
